import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Select, InputNumber, DatePicker, Button, Row, Col } from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Icons from '@/components/third-party/Icons';
import { defaultColors } from '@/themes/antd-theme';
import {
  get_product_list_both,
  get_recipe_by_product_id,
  get_recipe_for_schedule,
  generate_batch_number,
  create_production_demand,
} from '@/utils/api';
import {
  resolveRecipeById,
  filterFinalizedRecipes,
  buildVersionedRecipeSelectOptions,
} from '@/utils/recipe/recipeSelectOptions';

// ==============================|| CREATE PRODUCTION DEMAND (INLINE) ||============================== //

const CreateDemandForm = ({ open, onClose, onCreated, selectedModule, recipeTypeFlag }) => {
  const [form] = Form.useForm();
  const selectedRecipeId = Form.useWatch('recipe_id', form);
  const [productList, setProductList] = useState([]);
  const [recipeList, setRecipeList] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const finalizedRecipeIdsRef = useRef(new Set());
  const productRecipesRef = useRef([]);

  const recipeSelectOptions = useMemo(
    () => buildVersionedRecipeSelectOptions(recipeList),
    [recipeList]
  );

  const loadProducts = () => {
    get_product_list_both({ module_type: selectedModule, recipe_type: recipeTypeFlag }, (res) => {
      setProductList(res?.data?.data || []);
    });
  };

  useEffect(() => {
    if (!open) return;
    loadProducts();
    get_recipe_for_schedule({ module_type: selectedModule, recipe_type: recipeTypeFlag }, (res) => {
      const rows = res?.data?.status !== 'failed' ? res?.data?.data || [] : [];
      finalizedRecipeIdsRef.current = new Set(rows.map((r) => Number(r.recipe_id)));
      if (productRecipesRef.current.length) {
        setRecipeList(filterFinalizedRecipes(productRecipesRef.current, finalizedRecipeIdsRef.current));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedModule, recipeTypeFlag]);

  const handleProductChange = (productId) => {
    form.setFieldsValue({ recipe_id: undefined, required_quantity: undefined });
    setRecipeList([]);
    productRecipesRef.current = [];
    if (!productId) return;
    setRecipeLoading(true);
    const params = { product_id: productId, finalized_only: true };
    if (recipeTypeFlag) params.recipe_type = true;
    get_recipe_by_product_id((res) => {
      const rows = res?.data?.status === 'success' ? res?.data?.data || [] : [];
      productRecipesRef.current = rows;
      setRecipeList(filterFinalizedRecipes(rows, finalizedRecipeIdsRef.current));
      setRecipeLoading(false);
    }, params);
  };

  const handleCancel = () => {
    form.resetFields();
    setRecipeList([]);
    onClose();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);

    // batch_number is never user-typed here — generated silently the same way the
    // "Issue Batch" scheduling form does, from the chosen recipe.
    generate_batch_number((res) => {
      if (res?.data?.status !== 'success') {
        toast.error(res?.data?.message || 'Failed to generate batch number');
        setSubmitting(false);
        return;
      }
      const batch_number = res.data.data.batch_number;

      create_production_demand(
        {
          batch_number,
          product_id: values.product_id,
          recipe_id: values.recipe_id,
          required_quantity: values.required_quantity,
          start_date: values.dates[0].format('YYYY-MM-DD'),
          end_date: values.dates[1].format('YYYY-MM-DD'),
          priority: values.priority || 'MEDIUM',
        },
        (data) => {
          setSubmitting(false);
          if (data?.success) {
            toast.success(`Demand ${data.batch_number} created`);
            form.resetFields();
            setRecipeList([]);
            onCreated?.();
            onClose();
          } else {
            toast.error(data?.message || 'Failed to create demand');
          }
        },
        { onError: () => setSubmitting(false) }
      );
    }, { recipe_id: values.recipe_id, recipe_type: recipeTypeFlag });
  };

  if (!open) return null;

  return (
    <div
      className="rounded-xl border mb-2"
      style={{
        backgroundColor: '#ffffff',
        borderColor: defaultColors.primary,
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        padding: '20px 24px 12px',
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 rounded-md" style={{ backgroundColor: defaultColors.palette.teal[50] }}>
          <Icons.Plus className="w-4 h-4" style={{ color: defaultColors.primary }} />
        </div>
        <span className="text-sm font-semibold" style={{ color: defaultColors.primary }}>
          Create Production Demand
        </span>
      </div>

      <Form form={form} layout="vertical" initialValues={{ priority: 'MEDIUM' }}>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <Form.Item
              name="product_id"
              label="Product"
              rules={[{ required: true, message: 'Product is required' }]}
            >
              <Select
                showSearch
                placeholder="Select Product"
                optionFilterProp="children"
                onChange={handleProductChange}
              >
                {productList?.map((p) => (
                  <Select.Option key={p.product_id} value={p.product_id}>
                    {p.product_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              name="recipe_id"
              label="Recipe"
              rules={[{ required: true, message: 'Recipe is required' }]}
            >
              <Select
                showSearch
                placeholder="Select Recipe (finalized only)"
                options={recipeSelectOptions}
                optionLabelProp="label"
                loading={recipeLoading}
                notFoundContent={recipeLoading ? 'Loading...' : 'No finalized recipe for this product'}
                filterOption={(input, option) => {
                  if (option?.options) return false;
                  const label = option?.['data-label'] ?? option?.title ?? '';
                  return String(label).toLowerCase().includes(input.toLowerCase());
                }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                onChange={(recipeId) => {
                  const recipeData = resolveRecipeById(recipeList, recipeId);
                  const batchSize = recipeData?.master_batch_size;
                  form.setFieldsValue({
                    required_quantity:
                      batchSize != null && batchSize !== '' ? Number(batchSize) : undefined,
                  });
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              name="required_quantity"
              label="Required Quantity (Batch Size)"
              extra={
                selectedRecipeId
                  ? `Master Batch Size: ${
                      resolveRecipeById(recipeList, selectedRecipeId)?.master_batch_size ?? '—'
                    }`
                  : null
              }
              rules={[
                { required: true, message: 'Required quantity (batch size) is required' },
                { type: 'number', min: 0.01, message: 'Must be greater than 0' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0.01} placeholder="Enter quantity" />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              name="dates"
              label="Demand Window (Start - End Date)"
              rules={[{ required: true, message: 'Start and end date are required' }]}
            >
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                disabledDate={(current, { from } = {}) => {
                  if (current && current < dayjs().startOf('day')) return true;
                  if (from) return current && current < from.startOf('day');
                  return false;
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item name="priority" label="Priority">
              <Select>
                <Select.Option value="HIGH">High</Select.Option>
                <Select.Option value="MEDIUM">Medium</Select.Option>
                <Select.Option value="LOW">Low</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={0} md={8} />
        </Row>

        <div className="flex justify-end gap-2 mt-1 mb-2">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Create Demand
          </Button>
        </div>
      </Form>
    </div>
  );
};

CreateDemandForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  selectedModule: PropTypes.string,
  recipeTypeFlag: PropTypes.bool,
};

export default CreateDemandForm;
